<template>
  <v-container class="pa-0" fluid style="height: 100%; overflow: hidden;">

    <v-fade-transition hide-on-leave>
      <template v-if="loading">
        <v-row align="center" class="fill-height my-12" justify="center">
          <v-progress-circular size="64" indeterminate color="primary"/>
        </v-row>
      </template>
    </v-fade-transition>

    <template v-if="!loading">
      <v-row v-if="showPromptEditor">
        <v-container fluid style="background-color: lightgray">
          <v-col cols="6">
            <v-select v-model="editedIndex" :items="indices" label="Indeksi" outlined hide-details/>
          </v-col>
          <v-col cols="12">
            <v-alert type="info" color="black" outlined>
              Mukautettu prompt automaattisesti käytössä tässä istunnossa. Tuotantoon vienti erikseen "Vie tuotantoon"
              -napilla.
              Promptista tulee löytyä
              <v-chip>{context}</v-chip>
              ja
              <v-chip>{question}</v-chip>
              muuttujat
            </v-alert>
            <v-textarea outlined label="Prompt" v-model="prompt" hide-details/>
          </v-col>
          <v-col cols="12" class="mb-2">
            <v-btn @click="resetPrompt" :disabled="savingPrompt" :loading="resettingPrompt">Reset</v-btn>
            <v-btn @click="savePrompt" class="ml-2" :disabled="resettingPrompt" :loading="savingPrompt">Vie prompt
              tuotantoon
            </v-btn>
          </v-col>
        </v-container>
      </v-row>

      <v-row style="height: 82px; z-index: 1; position: relative; " no-gutters>
        <v-container
            class="rowQuestion chat-input-holder" fluid>
          <v-row justify="center">
            <v-col cols="12">
              <chat-input :loading="sendingMessage"
                          :temperature="temperature"
                          @submit="sendMessage"
                          @temperature="setTemperature"
                          @start-new-chat="startNewChat"/>
            </v-col>
          </v-row>
        </v-container>
      </v-row>

      <v-row justify="center" no-gutters style="max-height: calc(100% - 82px);overflow: auto;">
        <v-slide-y-transition hide-on-leave>
          <v-col v-if="messages.length > 0" cols="12">

            <template v-for="(message, index) in formattedMessages">
              <div :key="message.index">
                <chat-message-item :message="message" @vote-down="vote" @vote-up="vote"
                                   :loading="message.inflight || false"/>
                <v-divider v-if="index < formattedMessages.length - 1" class="my-0 py-0 rowDivider"/>
              </div>
            </template>
            <div :key="disclaimerMessage.index">
              <chat-message-item :message="disclaimerMessage"/>
            </div>
          </v-col>
        </v-slide-y-transition>
        <v-fade-transition hide-on-leave>
          <v-col v-if="messages.length === 0" class="fill-height" cols="12" md="10">
            <v-row align="center" class="fill-height" justify="center" no-gutters>
              <v-col class="my-7" cols="10">
                <div class="text-h6 accent2--text text-md-center">
                  {{ $t('ask_anything') }}
                </div>
                <div class="text-body-2 text-md-center">
                  {{ $t('ai_disclaimer') }}
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-fade-transition>

        <support-ticket-dialog ref="supportTicketDialog"/>

      </v-row>
    </template>

    <div id="chat-end"/>
  </v-container>
</template>

<script>
import ChatMessageItem from "@/views/EmbedChat/components/ChatMessageItem";
import ChatInput from "@/views/EmbedChat/components/ChatInput";
import chatApi from "@/api/chat-api";
import axios from "axios";
import store from "@/store";
import {useSessionStorage} from "@vueuse/core";
import {v4 as uuidv4} from 'uuid';
import SupportTicketDialog from "@/views/EmbedChat/components/SupportTicketDialog.vue";
import {fetchEventSource} from '@microsoft/fetch-event-source';

let chatStorage;

function getTopLevelDomain(widgetConfig = null) {
  // First check if language is provided via widget config
  if (widgetConfig && widgetConfig.language) {
    const language = widgetConfig.language;
    if (language === 'sv') {
      return 'se';
    }
    if (language === 'en') {
      return 'com';
    }
    return language;
  }

  // Fallback to URL params (for iframe embedding)
  if (window.location && window.location.search) {
    const queryString = window.location.search;
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      const language = urlParams.get('language');
      if (language) {
        if (language === 'sv') {
          return 'se';
        }
        if (language === 'en') {
          return 'com';
        }
        return language;
      }
    }
  }
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : 'fi';
}

function resolveEnv() {
  try {
    return window.parent.location === window.location ? 'standalone' : 'embedded';
  } catch (error) {
    console.log(error);
    return 'embedded_err';
  }
}

export default {
  name: "EmbedChatView",
  components: {SupportTicketDialog, ChatInput, ChatMessageItem},
  data() {
    return {
      messages: [],
      temperature: 20,
      loading: true,
      subtleLoading: true,
      sendingMessage: false,
      activeChat: null,
      chatId: null,
      pastChats: [],
      user: null,
      domainEnd: null,
      showPromptEditor: false,
      savingPrompt: false,
      resettingPrompt: false,
      editedIndex: null,
      k: null,
      indices: ['fi', 'com', 'de', 'fr', 'se', 'nl'],
    };
  },
  computed: {
    disclaimerMessage() {
      return {
        id: 'disclaimer_message',
        index: -1,
        message: this.$t('ai_disclaimer'),
        type: 'reply'
      }
    },
    formattedMessages() {
      let index = 0;
      const messages = [...this.messages].reverse();
      return messages.flatMap(m => ([
        {
          ...m,
          id: `${m.id}-a-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'reply',
          message: m.reply,
          index: index++,
          selectedIndex: m.index,
          sourceMetadata: m.source,
          sourceUrl: m.sourceUrl,
          temperature: m.temperature,
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
          inflight: m.inflight,
        },
        {
          ...m,
          id: `${m.id}-q-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'question',
          message: m.message,
          index: index++,
          selectedIndex: m.index,
          sourceMetadata: m.source,
          sourceUrl: m.sourceUrl,
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
          temperature: m.temperature
        }]))
    }
  },
  watch: {
    editedIndex(to, from) {
      if (from) {
        this.resetPrompt();
      }
    },
    domainEnd(val) {
      this.setLocale(val);
    },
  },
  methods: {
    async resetPrompt() {
      this.resettingPrompt = true;
      try {
        await this.reloadPrompt();
      } catch (error) {
        alert(error);
      }
      this.resettingPrompt = false;
    },
    async savePrompt() {
      this.savingPrompt = true;
      try {
        if (confirm("Haluatko varmasti päivittää promptin tuotantoon?" + this.editedIndex)) {
          await chatApi.savePrompt(this.prompt, this.editedIndex, this.k);
        }
      } catch (error) {
        this.alert(error);
      }

      this.savingPrompt = false;
    },
    async reloadPrompt() {
      if (this.editedIndex == null) {
        const editedIndex = getTopLevelDomain();
        if (!(editedIndex in this.indices)) {
          this.editedIndex = 'fi';
        } else {
          this.editedIndex = editedIndex;
        }
      }

      const prompt = await chatApi.getPrompt(this.editedIndex, this.k);
      this.prompt = prompt;
    },
    setLocale(val) {
      if (val === 'com') {
        this.$i18n.locale = 'en';
      } else if (val) {
        this.$i18n.locale = val;
      } else {
        this.$i18n.locale = 'en';
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('fi-FI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    },
    viewChat(chat) {
      this.chatId = chat.id;
      this.activeChat = chat.timestamp;
      this.reloadMessages();
    },
    async reloadMessages() {
      if (this.loading === false) {
        this.subtleLoading = true;
      } else {
        this.loading = true;
      }
      try {
        const messages = await chatApi.getChatMessages(this.chatId);
        messages.sort((a, b) => a.timestamp - b.timestamp);
        this.messages = messages || [];
        //this.scrollToEnd();
      } catch (error) {
        alert(error);
      }
      this.loading = false;
      this.subtleLoading = false;
    },
    async sendMessage(messageText) {
      this.sendingMessage = true;
      if (this.chatId.startsWith('dummy')) {
        await this.createNewChat(messageText);
      }

      this.$sendAnalyticsEvent('chat-message-sent');

      const uuid = uuidv4();
      const placeholderChatMessage = {
        id: uuid,
        chat_id: this.chatId,
        message: messageText,
        timestamp: new Date(),
        error: false,
        temperature: this.temperature / 100,
        lastUpdatedTimestamp: new Date().getTime(),
        indexName: getTopLevelDomain(),
        sourceUrl: '',
        reply: '',
        inflight: true,
      };

      try {
        const previousMessage = this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;

        this.messages.push(placeholderChatMessage);
        //this.scrollToEnd();
        /*const chatMessage = await chatApi.newChatMessage(this.chatId, {
          message: messageText,
          chatId: this.chatId,
          temperature: this.temperature / 100,
          chatMessage: previousMessage?.chat_message,
          indexName: getTopLevelDomain(),
          customPrompt: this.prompt
        });*/
        const placeholderChatMessageIndex = this.messages.findIndex(m => m.id === uuid)

        const ctrl = new AbortController();
        let chatMessage = [];
        try {
          const baseURL = axios.defaults.baseURL || '';
          await fetchEventSource(`${baseURL}/api/chat/${this.chatId}/message-stream`, {
            method: 'POST',
            body: JSON.stringify({

              message: messageText,
              chatId: this.chatId,
              temperature: this.temperature / 100,
              chatMessage: previousMessage?.chat_message || '',
              timestamp: new Date().getTime(),
              indexName: getTopLevelDomain(),
              customPrompt: this.prompt
            }),
            headers: {
              'accept-encoding': '',
              'Content-Type': 'application/json',
              'x-api-key': axios.defaults.headers.common['x-api-key'],
              'Accept': 'text/event-stream'
            },
            signal: ctrl.signal,
            openWhenHidden: true,
            onmessage: (ev) => {
              const msg = ev.data.replaceAll('___|n|___', '\n')
              // ev.data, ev.event, ev.id
              if (ev.event === 'id') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  id: msg,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'source_url') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  sourceUrl: msg,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'error') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  error: true,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'message') {
                chatMessage.push(msg);
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  reply: chatMessage.join(""),
                  lastUpdatedTimestamp: new Date().getTime()

                });
                /*if (this.autoScrollEnabled) {
                  this.$nextTick(() => {
                    this.scrollToEnd();
                  })
                }*/
              }
            }
          })
        } catch (e) {
          console.error(e)
        } finally {
          ctrl.abort();
        }
        Object.assign(this.messages[placeholderChatMessageIndex], {
          ...this.messages[placeholderChatMessageIndex],
          lastUpdatedTimestamp: new Date().getTime(),
          inflight: false
        });
        //this.scrollToEnd();
      } catch (error) {
        console.log(error);
        const messageIndex = this.messages.findIndex(m => m.id === uuid);
        if (messageIndex > -1) {
          placeholderChatMessage.error = true;
          Object.assign(this.messages[messageIndex], placeholderChatMessage);
        }
      }
      this.sendingMessage = false;
    },
    scrollToEnd() {
      this.$nextTick(() => {
        this.$vuetify.goTo('#chat-end', {duration: 0});
      })
    },
    async reloadChats() {
      try {
        const chats = await chatApi.getChats();
        this.pastChats = chats.sort((a, b) => {
          return b.timestamp.getTime() - a.timestamp.getTime();
        }) || [];
      } catch (error) {
        alert(error);
      }
    },
    async startNewChat() {
      const timestamp = new Date().getTime();
      const dummyChat = {
        id: `dummy-${timestamp}`,
        name: 'Uusi keskustelu',
        timestamp: timestamp,
      };

      this.chatId = dummyChat.id;
      this.pastChats.unshift(dummyChat);
      this.activeChat = dummyChat.id;
      // await this.reloadMessages();
      this.messages = [];
    },
    async createNewChat(messageText) {
      try {
        const env = resolveEnv();

        const chat = await chatApi.newChat({name: messageText, environment: env});
        this.chatId = chat.id;
        this.activeChat = chat.id;
        chatStorage.value.chatId = this.chatId;
        await this.reloadChats();
        await this.reloadMessages();
      } catch (error) {
        alert(error);
      }
    },
    setTemperature(value) {
      this.temperature = value;
    },
    async vote(message, vote) {
      const messages = [...this.messages];
      const newRating = message.rating !== vote ? vote : 0;
      try {
        const data = await chatApi.rating(message, newRating);
        const newMessage = data.response[0];
        const index = messages.findIndex(m => m.id === newMessage.id);
        if (index > -1) {
          messages[index] = {...messages[index], rating: newMessage.rating};
          this.messages = messages;
        }
        if (newRating === -1) {
          this.$refs.supportTicketDialog.open(message);
        }
      } catch (e) {
        console.error(e);
        alert(e);
      }
    },
  },
  async mounted() {
    // Get widget config from root instance (if embedded as widget)
    const widgetConfig = this.$root.widgetConfig || null;

    // Set API key from widget config or session ID
    if (widgetConfig && widgetConfig.apiKey) {
      axios.defaults.headers.common['x-api-key'] = widgetConfig.apiKey;
    } else {
      axios.defaults.headers.common['x-api-key'] = store.state.sessionId;
    }

    // Jos query stringissä dev=true
    if (window.location.search.includes('dev=')) {
      const params = new URLSearchParams(window.location.search);
      const devKey = params.get('dev');
      this.k = devKey;
      await this.reloadPrompt();
      this.showPromptEditor = true;
    }

    await this.reloadChats();

    chatStorage = useSessionStorage('chat-store', {chatId: store.state.sessionId})

    this.startNewChat();
    this.loading = false;

    this.domainEnd = getTopLevelDomain(widgetConfig);
    this.setLocale(this.domainEnd);

    this.$nextTick(() => {
      // Check for search query in widget config first, then URL params
      let searchQuery = null;

      if (widgetConfig && widgetConfig.searchQuery) {
        searchQuery = widgetConfig.searchQuery;
      } else {
        const queryString = window.location.search;
        if (queryString) {
          const urlParams = new URLSearchParams(queryString);
          searchQuery = urlParams.get('search_api_fulltext');
        }
      }

      if (searchQuery) {
        this.$t('searching_information_about_subject')
        this.sendMessage(`${this.$t('searching_information_about_subject')}: ${searchQuery}`);
      }
    });
  }
}
</script>

<style lang="scss">
.chat-input-holder {
  -webkit-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  -moz-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
}

.new-chat-button-holder {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.user-info-holder {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

#chat-end {
  position: absolute;
  bottom: 0;
}
</style>

